import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { PageFlip } from "page-flip";
import "./AURPageFlipPreview.css";
import { useLanguage } from "../shared/LanguageContext";
import { websiteCopy } from "../shared/websiteCopy";
import {
  getPreparedPreviewImage,
  getPreviewImageLimits,
  preparePreview,
} from "../shared/previewImageCache";

const DEFAULT_PAGE_WIDTH = 550;
const DEFAULT_PAGE_HEIGHT = 733;
const MAX_PAGE_WIDTH = 1100;
const MAX_PAGE_HEIGHT = Math.ceil(
  MAX_PAGE_WIDTH * (DEFAULT_PAGE_HEIGHT / DEFAULT_PAGE_WIDTH)
);
const MAX_DPR = 2;

const MIN_ZOOM_SCALE = 1;
const MAX_ZOOM_SCALE = 3;
const SWIPE_THRESHOLD = 12;

const AURPageFlipPreview = forwardRef(function AURPageFlipPreview(
  {
    currentPage,
    onPageChange,
    firstPage = 1,
    lastPage = 16,
    pageWidth = DEFAULT_PAGE_WIDTH,
    pageHeight = DEFAULT_PAGE_HEIGHT,
    edition = "english",
  },
  ref
) {
  const { language } = useLanguage();
  const copy = websiteCopy[language].preview;
  const pageFlipRef = useRef(null);
  const bookRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const rafRef = useRef(null);
  const zoomRafRef = useRef(null);
  const readyRef = useRef(false);

  const zoomRef = useRef({
    scale: MIN_ZOOM_SCALE,
    panX: 0,
    panY: 0,
  });

  const gestureRef = useRef({
    mode: "idle",
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    initialDistance: 0,
    initialScale: MIN_ZOOM_SCALE,
    initialPanX: 0,
    initialPanY: 0,
    initialMidpointX: 0,
    initialMidpointY: 0,
  });

  const getBookCanvas = () => {
    const root = bookRef.current;
    if (!root) return null;
    return root.querySelector("canvas.stf__canvas");
  };

  const getZoomBounds = (scale) => {
    const root = bookRef.current;
    const canvas = getBookCanvas();

    if (!root || !canvas) {
      return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
    }

    const rootRect = root.getBoundingClientRect();
    const baseWidth = canvas.offsetWidth || canvas.clientWidth;
    const baseHeight = canvas.offsetHeight || canvas.clientHeight;

    if (!baseWidth || !baseHeight) {
      return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
    }

    const scaledWidth = baseWidth * scale;
    const scaledHeight = baseHeight * scale;

    const centeredX =
      scaledWidth < rootRect.width
        ? (rootRect.width - scaledWidth) / 2
        : 0;
    const centeredY =
      scaledHeight < rootRect.height
        ? (rootRect.height - scaledHeight) / 2
        : 0;

    return {
      minX:
        scaledWidth > rootRect.width
          ? rootRect.width - scaledWidth
          : centeredX,
      maxX:
        scaledWidth > rootRect.width
          ? 0
          : centeredX,
      minY:
        scaledHeight > rootRect.height
          ? rootRect.height - scaledHeight
          : centeredY,
      maxY:
        scaledHeight > rootRect.height
          ? 0
          : centeredY,
    };
  };

  const clampZoomPan = (scale, panX, panY) => {
    if (scale <= MIN_ZOOM_SCALE) {
      return { scale: MIN_ZOOM_SCALE, panX: 0, panY: 0 };
    }

    const bounds = getZoomBounds(scale);

    return {
      scale,
      panX: Math.min(bounds.maxX, Math.max(bounds.minX, panX)),
      panY: Math.min(bounds.maxY, Math.max(bounds.minY, panY)),
    };
  };

  const applyZoomTransform = () => {
    const canvas = getBookCanvas();
    if (!canvas) return;

    const state = clampZoomPan(
      zoomRef.current.scale,
      zoomRef.current.panX,
      zoomRef.current.panY
    );

    zoomRef.current = state;
    canvas.style.transformOrigin = "0 0";
    canvas.style.transform =
      `translate3d(${state.panX}px, ${state.panY}px, 0) ` +
      `scale(${state.scale})`;
    canvas.style.willChange =
      state.scale > MIN_ZOOM_SCALE ? "transform" : "auto";
  };

  const scheduleZoomTransform = () => {
    if (typeof window === "undefined") return;

    if (zoomRafRef.current !== null) {
      window.cancelAnimationFrame(zoomRafRef.current);
    }

    zoomRafRef.current = window.requestAnimationFrame(() => {
      applyZoomTransform();
      zoomRafRef.current = null;
    });
  };

  const resetZoom = () => {
    zoomRef.current = {
      scale: MIN_ZOOM_SCALE,
      panX: 0,
      panY: 0,
    };
    scheduleZoomTransform();
  };

  const getDistance = (a, b) =>
    Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);

  const getMidpoint = (a, b) => ({
    x: (a.clientX + b.clientX) / 2,
    y: (a.clientY + b.clientY) / 2,
  });

  const beginPinch = (touchA, touchB, root) => {
    const midpoint = getMidpoint(touchA, touchB);
    const rect = root.getBoundingClientRect();

    gestureRef.current = {
      mode: "pinch",
      startX: 0,
      startY: 0,
      lastX: 0,
      lastY: 0,
      initialDistance: Math.max(1, getDistance(touchA, touchB)),
      initialScale: zoomRef.current.scale,
      initialPanX: zoomRef.current.panX,
      initialPanY: zoomRef.current.panY,
      initialMidpointX: midpoint.x - rect.left,
      initialMidpointY: midpoint.y - rect.top,
    };
  };

  const updatePinch = (touchA, touchB) => {
    const gesture = gestureRef.current;
    if (!gesture.initialDistance) return;

    const root = bookRef.current;
    if (!root) return;

    const currentDistance = getDistance(touchA, touchB);
    const scale = Math.min(
      MAX_ZOOM_SCALE,
      Math.max(
        MIN_ZOOM_SCALE,
        gesture.initialScale *
          (currentDistance / gesture.initialDistance)
      )
    );

    const rect = root.getBoundingClientRect();
    const midpoint = getMidpoint(touchA, touchB);

    const midpointX = midpoint.x - rect.left;
    const midpointY = midpoint.y - rect.top;

    const scaleRatio =
      scale / (gesture.initialScale || MIN_ZOOM_SCALE);

    const anchoredPanX =
      midpointX -
      gesture.initialMidpointX * scaleRatio +
      gesture.initialPanX * scaleRatio;

    const anchoredPanY =
      midpointY -
      gesture.initialMidpointY * scaleRatio +
      gesture.initialPanY * scaleRatio;

    zoomRef.current = clampZoomPan(
      scale,
      scale === MIN_ZOOM_SCALE ? 0 : anchoredPanX,
      scale === MIN_ZOOM_SCALE ? 0 : anchoredPanY
    );

    scheduleZoomTransform();
  };

  const applyHighDpiCanvas = () => {
    const root = bookRef.current;
    if (!root) return;

    const canvas = root.querySelector("canvas.stf__canvas");
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const dpr = Math.min(
      MAX_DPR,
      Math.max(
        1,
        Number(
          typeof window !== "undefined" && window.devicePixelRatio
            ? window.devicePixelRatio
            : 1
        )
      )
    );

    const cssWidth = Math.round(rect.width);
    const cssHeight = Math.round(rect.height);
    const targetWidth = Math.max(1, Math.round(cssWidth * dpr));
    const targetHeight = Math.max(1, Math.round(cssHeight * dpr));

    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;

    if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
      canvas.width = targetWidth;
      canvas.height = targetHeight;
    }

    const context = canvas.getContext("2d");
    if (!context) return;

    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    context.imageSmoothingEnabled = true;

    if ("imageSmoothingQuality" in context) {
      context.imageSmoothingQuality = "high";
    }

    scheduleZoomTransform();
  };

  const scheduleHighDpiCanvas = () => {
    if (typeof window === "undefined") return;

    if (rafRef.current !== null) {
      window.cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = window.requestAnimationFrame(() => {
        applyHighDpiCanvas();
        rafRef.current = null;
      });
    });
  };

  const flipNext = () => {
    const pageFlip = pageFlipRef.current;
    if (!pageFlip || !readyRef.current) return false;

    if (pageFlip.getCurrentPageIndex() >= lastPage - firstPage) {
      return false;
    }

    resetZoom();
    pageFlip.flipNext("bottom");
    return true;
  };

  const flipPrevious = () => {
    const pageFlip = pageFlipRef.current;
    if (!pageFlip || !readyRef.current) return false;
    if (pageFlip.getCurrentPageIndex() <= 0) return false;

    resetZoom();

    const bounds = pageFlip.getBoundsRect();
    const flipController = pageFlip.getFlipController();

    flipController.flip({
      x: bounds.left + 10,
      y: 1,
    });

    return true;
  };

  const handleTouchStart = (event) => {
    const root = bookRef.current;
    if (!root || !event.touches || event.touches.length === 0) return;

    if (event.touches.length >= 2) {
      beginPinch(event.touches[0], event.touches[1], root);
      event.preventDefault();
      return;
    }

    const touch = event.touches[0];

    gestureRef.current = {
      mode:
        zoomRef.current.scale > MIN_ZOOM_SCALE ? "pan" : "swipe",
      startX: touch.clientX,
      startY: touch.clientY,
      lastX: touch.clientX,
      lastY: touch.clientY,
      initialDistance: 0,
      initialScale: zoomRef.current.scale,
      initialPanX: zoomRef.current.panX,
      initialPanY: zoomRef.current.panY,
      initialMidpointX: 0,
      initialMidpointY: 0,
    };
  };

  const handleTouchMove = (event) => {
    if (!event.touches || event.touches.length === 0) return;

    if (event.touches.length >= 2) {
      if (gestureRef.current.mode !== "pinch") {
        beginPinch(event.touches[0], event.touches[1], bookRef.current);
      }

      updatePinch(event.touches[0], event.touches[1]);
      event.preventDefault();
      return;
    }

    if (gestureRef.current.mode !== "pan") return;

    const touch = event.touches[0];
    const gesture = gestureRef.current;
    const dx = touch.clientX - gesture.lastX;
    const dy = touch.clientY - gesture.lastY;

    gesture.lastX = touch.clientX;
    gesture.lastY = touch.clientY;

    zoomRef.current = clampZoomPan(
      zoomRef.current.scale,
      zoomRef.current.panX + dx,
      zoomRef.current.panY + dy
    );

    scheduleZoomTransform();
    event.preventDefault();
  };

  const handleTouchEnd = (event) => {
    const gesture = gestureRef.current;

    if (event.touches && event.touches.length >= 2) return;

    if (event.touches && event.touches.length === 1) {
      const touch = event.touches[0];

      if (zoomRef.current.scale > MIN_ZOOM_SCALE) {
        gestureRef.current = {
          ...gesture,
          mode: "pan",
          lastX: touch.clientX,
          lastY: touch.clientY,
        };
      } else {
        gestureRef.current = {
          ...gesture,
          mode: "swipe",
          startX: touch.clientX,
          startY: touch.clientY,
          lastX: touch.clientX,
          lastY: touch.clientY,
        };
      }

      return;
    }

    gestureRef.current = { ...gesture, mode: "idle" };

    if (gesture.mode === "pinch") {
      if (zoomRef.current.scale <= MIN_ZOOM_SCALE) {
        resetZoom();
      }
      return;
    }

    if (gesture.mode === "pan") return;
    if (gesture.mode !== "swipe") return;
    if (!event.changedTouches || !event.changedTouches.length) return;

    const touch = event.changedTouches[0];
    const dx = touch.clientX - gesture.startX;
    const dy = touch.clientY - gesture.startY;

    if (
      Math.abs(dx) >= SWIPE_THRESHOLD &&
      Math.abs(dx) > Math.abs(dy)
    ) {
      if (dx < 0) flipNext();
      else flipPrevious();
      return;
    }

    if (
      Math.abs(dx) >= SWIPE_THRESHOLD ||
      Math.abs(dy) >= SWIPE_THRESHOLD
    ) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width) return;

    if (touch.clientX >= rect.left + rect.width / 2) {
      flipNext();
    } else {
      flipPrevious();
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      next: flipNext,
      previous: flipPrevious,
      isReady() {
        return readyRef.current;
      },
      getCurrentPage() {
        const pageFlip = pageFlipRef.current;
        if (!pageFlip) return null;
        return firstPage + pageFlip.getCurrentPageIndex();
      },
    }),
    [firstPage, lastPage]
  );

  useEffect(() => {
    const root = bookRef.current;
    if (!root) return undefined;

    root.style.touchAction = "none";
    root.style.overscrollBehavior = "contain";

    root.addEventListener("touchstart", handleTouchStart, {
      passive: false,
      capture: true,
    });
    root.addEventListener("touchmove", handleTouchMove, {
      passive: false,
      capture: true,
    });
    root.addEventListener("touchend", handleTouchEnd, {
      passive: false,
      capture: true,
    });
    root.addEventListener("touchcancel", handleTouchEnd, {
      passive: false,
      capture: true,
    });

    return () => {
      root.removeEventListener("touchstart", handleTouchStart, true);
      root.removeEventListener("touchmove", handleTouchMove, true);
      root.removeEventListener("touchend", handleTouchEnd, true);
      root.removeEventListener("touchcancel", handleTouchEnd, true);
    };
  }, []);

  useEffect(() => {
    resetZoom();
  }, [edition, firstPage, lastPage]);

  useEffect(() => {
    preparePreview(edition);
  }, [edition]);

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      if (cancelled || !bookRef.current) return;

      const effectiveLastPage = Math.min(
        lastPage,
        firstPage + getPreviewImageLimits(edition) - 1
      );

      const waitForImages = async () => {
        const urls = [];

        for (
          let page = firstPage;
          page <= effectiveLastPage;
          page += 1
        ) {
          let url = getPreparedPreviewImage(edition, page);

          while (!url && !cancelled) {
            await new Promise((resolve) =>
              window.setTimeout(resolve, 100)
            );
            url = getPreparedPreviewImage(edition, page);
          }

          if (cancelled) return null;
          urls.push(url);
        }

        return urls;
      };

      const loadedImages = await waitForImages();
      if (cancelled || !bookRef.current || !loadedImages) return;

      const pageFlip = new PageFlip(bookRef.current, {
        width: pageWidth,
        height: pageHeight,
        size: "stretch",
        minWidth: 280,
        maxWidth: MAX_PAGE_WIDTH,
        minHeight: 420,
        maxHeight: MAX_PAGE_HEIGHT,
        drawShadow: true,
        flippingTime: 650,
        usePortrait: true,
        autoSize: true,
        maxShadowOpacity: 0.35,
        showCover: false,
        mobileScrollSupport: false,
        useMouseEvents: false,
        disableFlipByClick: true,
      });

      pageFlipRef.current = pageFlip;

      pageFlip.on("flip", (event) => {
        const absolutePage = firstPage + Number(event.data);
        onPageChange(absolutePage);
        scheduleHighDpiCanvas();
      });

      pageFlip.on("init", () => {
        readyRef.current = true;
        scheduleHighDpiCanvas();
      });

      pageFlip.loadFromImages(loadedImages);

      readyRef.current = true;
      scheduleHighDpiCanvas();

      if (typeof ResizeObserver !== "undefined") {
        resizeObserverRef.current = new ResizeObserver(() => {
          scheduleHighDpiCanvas();
        });
        resizeObserverRef.current.observe(bookRef.current);
      }
    };

    const initRaf = window.requestAnimationFrame(() => {
      initialize();
    });

    return () => {
      cancelled = true;
      readyRef.current = false;

      window.cancelAnimationFrame(initRaf);

      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      if (zoomRafRef.current !== null) {
        window.cancelAnimationFrame(zoomRafRef.current);
        zoomRafRef.current = null;
      }

      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }

      if (pageFlipRef.current) {
        pageFlipRef.current.destroy();
        pageFlipRef.current = null;
      }

      resetZoom();
    };
  }, [
    firstPage,
    lastPage,
    onPageChange,
    pageHeight,
    pageWidth,
    edition,
  ]);

  useEffect(() => {
    const pageFlip = pageFlipRef.current;
    if (!pageFlip || !readyRef.current) return;

    const targetIndex = Math.max(
      0,
      Math.min(lastPage - firstPage, currentPage - firstPage)
    );

    if (pageFlip.getCurrentPageIndex() !== targetIndex) {
      pageFlip.turnToPage(targetIndex);
      scheduleHighDpiCanvas();
    }
  }, [currentPage, firstPage, lastPage]);

  return (
    <div
      ref={bookRef}
      className="aur-page-flip-preview__book"
      aria-label={copy.aria}
    />
  );
});

AURPageFlipPreview.displayName = "AURPageFlipPreview";

export default AURPageFlipPreview;
